import { Controller } from "@hotwired/stimulus";
import { effect } from "@preact/signals-core";
import { uuid } from "../live/uuid";
import { checkPicture, csrfToken, fill, megabytes, type Sending, sendPicture } from "../maps/picture_upload";
import type { LiveScene } from "../types/generated/live";
import type MapBoard from "./map_board_controller";

type Labels = Record<"dimensions" | "preparing" | "replaced" | "sending" | "size" | "stopped" | "type", string>;

// Connects to data-controller="map-picture-swap", on the Imagem row of Configurações (an uploaded picture's map only).
// Trocar imagem sends the new file to the map, with the progress shown in the row; the answer goes through the board's live
// core like any write, so every board loads the new picture at once and no page refreshes. The row follows the scene's size
// and picture, so a replacement made from another tab shows here too.
export default class extends Controller<HTMLElement> {
	static targets = ["input", "replace", "progress", "status", "bar", "alert", "notice", "size", "thumb"];
	static values = { url: String, maxBytes: Number, labels: Object };
	declare readonly inputTarget: HTMLInputElement;
	declare readonly replaceTarget: HTMLButtonElement;
	declare readonly progressTarget: HTMLElement;
	declare readonly statusTarget: HTMLElement;
	declare readonly barTarget: HTMLElement;
	declare readonly alertTarget: HTMLElement;
	declare readonly noticeTarget: HTMLElement;
	declare readonly sizeTarget: HTMLElement;
	declare readonly thumbTarget: HTMLImageElement;
	declare readonly urlValue: string;
	declare readonly maxBytesValue: number;
	declare readonly labelsValue: Labels;
	sending?: Sending;
	shown?: string;
	dispose?: () => void;

	disconnect() {
		this.dispose?.();
		this.sending?.abort();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(
			this.element.closest('[data-controller~="map-board"]') as Element,
			"map-board",
		) as MapBoard | null;
	}

	ready() {
		const { state } = this.board as MapBoard;
		this.shown = state.view.peek().scene?.background_url;
		this.dispose?.();
		this.dispose = effect(() => this.follow(state.view.value.scene));
	}

	follow(scene: LiveScene | null) {
		if (!scene) return;
		const number = new Intl.NumberFormat(this.locale);
		this.sizeTarget.textContent = fill(this.labelsValue.dimensions, { width: number.format(scene.width_px), height: number.format(scene.height_px) });
		// The renderer already holds the new picture, so the thumbnail borrows it instead of waiting for a variant.
		if (scene.background_url && scene.background_url !== this.shown) {
			this.shown = scene.background_url;
			this.thumbTarget.src = scene.background_url;
		}
	}

	browse() {
		this.inputTarget.click();
	}

	async choose() {
		const file = this.inputTarget.files?.[0];
		this.inputTarget.value = "";
		if (!file || this.sending || !this.board?.state) return;
		this.noticeTarget.hidden = true;
		this.alertTarget.hidden = true;
		const failure = checkPicture(file, this.maxBytesValue);
		if (failure) {
			const { type, size } = this.labelsValue;
			const limit = megabytes(this.maxBytesValue, this.locale);
			return this.alert(failure === "type" ? fill(type, { name: file.name }) : fill(size, { name: file.name, size: megabytes(file.size, this.locale), limit }));
		}
		const form = new FormData();
		form.set("map_scene[picture]", file);
		form.set("mutation_id", uuid());
		this.working(true);
		this.progress(0, file.size);
		this.sending = sendPicture({
			url: this.urlValue,
			method: "PATCH",
			form,
			csrf: csrfToken(),
			onProgress: (loaded, total) => this.progress(loaded, total),
			onSent: () => {
				this.statusTarget.textContent = this.labelsValue.preparing;
				this.barTarget.style.width = "100%";
			},
		});
		const answer = await this.sending.done;
		this.sending = undefined;
		this.working(false);
		if (answer.kind === "aborted") return;
		if (answer.kind === "failed") return this.alert(this.labelsValue.stopped);
		if (answer.status >= 300) return this.alert(answer.body?.error ?? this.labelsValue.stopped);
		this.board?.applyAnswer(answer.body ?? {});
		this.noticeTarget.hidden = false;
	}

	progress(loaded: number, total: number) {
		this.statusTarget.textContent = fill(this.labelsValue.sending, { sent: megabytes(loaded, this.locale), total: megabytes(total, this.locale) });
		this.barTarget.style.width = `${total ? Math.min(100, (loaded / total) * 100) : 0}%`;
	}

	working(on: boolean) {
		this.progressTarget.hidden = !on;
		this.replaceTarget.hidden = on;
	}

	alert(message: string) {
		this.alertTarget.textContent = message;
		this.alertTarget.hidden = false;
	}

	get locale() {
		return document.documentElement.lang || "pt-BR";
	}
}
